/**
 * Thin data-access layer for the Order Booking designer panel.
 *
 * The shared axios wrapper (`src/services/api.js`) resolves with the parsed
 * envelope `{ code, status, message, data }` on success, and with the raw
 * axios error *response* on an HTTP error (so the envelope sits under
 * `.data`). `unwrap` collapses both into "payload or throw".
 */

import API from '../../../services/api';
import axios from '../../../services/axios';
import { ENDPOINTS, PAGE_SIZE } from './config';

export const isCancel = (err) => axios.isCancel(err);

export const createCancelSource = () => axios.CancelToken.source();

const GENERIC_ERROR = 'Something went wrong. Please try again.';

/** Pull the most useful message out of either envelope shape. */
export const errorMessageOf = (res, fallback = GENERIC_ERROR) => {
  if (!res) return fallback;
  if (res instanceof Error) return res.message || fallback;
  return res?.data?.message || res?.message || fallback;
};

const unwrap = (res, fallback) => {
  if (res && res.code === 200) return res.data;
  const err = new Error(errorMessageOf(res, fallback));
  err.payload = res;
  throw err;
};

const post = async ({ url, data, cancelToken, headers, onUploadProgress, fallback }) => {
  const res = await API({
    url,
    method: 'POST',
    responseType: 'json',
    data,
    cancelToken,
    headers,
    onUploadProgress,
  });
  return unwrap(res, fallback);
};

/** `{ assigned, picked, submitted, rejected, approved, total }` */
export const fetchCounts = (filters, cancelToken) =>
  post({
    url: ENDPOINTS.counts,
    cancelToken,
    fallback: 'Unable to load the tab counts.',
    data: {
      sub_type: filters?.sub_type || null,
      query: String(filters?.query || '').trim(),
      start_date: filters?.start_date || null,
      end_date: filters?.end_date || null,
    },
  });

/** `{ data: Row[], total, hasMore }` */
export const fetchList = ({ tab, page, limit = PAGE_SIZE, ...filters }, cancelToken) =>
  post({
    url: ENDPOINTS.list,
    cancelToken,
    fallback: 'Unable to load creatives.',
    data: {
      tab,
      page,
      limit,
      sub_type: filters?.sub_type || null,
      query: String(filters?.query || '').trim(),
      start_date: filters?.start_date || null,
      end_date: filters?.end_date || null,
      sort_by_latest_activity: !!filters?.sort_by_latest_activity,
    },
  });

/** A single Row. */
export const fetchDetail = (bsc_id, cancelToken) =>
  post({
    url: ENDPOINTS.detail,
    data: { bsc_id },
    cancelToken,
    fallback: 'Unable to load this creative.',
  });

/** `{ data: Log[] }` — oldest first. */
export const fetchTimeline = (bsc_id, cancelToken) =>
  post({
    url: ENDPOINTS.timeline,
    data: { bsc_id },
    cancelToken,
    fallback: 'Unable to load the activity for this creative.',
  });

/** `{ data: Version[] }` — newest first. */
export const fetchVersions = (bsc_id, cancelToken) =>
  post({
    url: ENDPOINTS.versions,
    data: { bsc_id },
    cancelToken,
    fallback: 'Unable to load the version history.',
  });

/** `{ bsc_id, status: 3, logs: Log[] }` */
export const pickCreative = (bsc_id) =>
  post({ url: ENDPOINTS.pick, data: { bsc_id }, fallback: 'Unable to pick this creative.' });

/** `{ logs: Log[] }` */
export const sendMessage = (bsc_id, message) =>
  post({
    url: ENDPOINTS.message,
    data: { bsc_id, message },
    fallback: 'Unable to send your message.',
  });

const uploadTo = (url, { bsc_id, file, message, skip_log, thumbnail_url, onUploadProgress }) => {
  const form = new FormData();
  form.append('files', file);
  form.append('bsc_id', bsc_id);
  if (message) form.append('message', message);
  // Video creatives upload a thumbnail then a video as two calls but must produce one combined
  // version — skip_log suppresses the thumbnail call's own log write, thumbnail_url folds it
  // into the video call's combined entry. See UploadDesignModal.js's handleUpload.
  if (skip_log) form.append('skip_log', 'true');
  if (thumbnail_url) form.append('thumbnail_url', thumbnail_url);
  return post({
    url,
    data: form,
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
    fallback: 'Upload failed. Please try again.',
  });
};

/** All three resolve with `{ bsc_id, url, status, logs: Log[] }`. */
export const uploadImage = (payload) => uploadTo(ENDPOINTS.uploadImage, payload);
export const uploadVideo = (payload) => uploadTo(ENDPOINTS.uploadVideo, payload);
export const uploadAttachment = (payload) => uploadTo(ENDPOINTS.uploadAttachment, payload);
